import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Users, Search, Loader2, Star as StarIcon, Trash2, Sparkles } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MusicianMatchCard from "@/components/MusicianMatchCard";
import { EmptyState } from "@/components/common";

const INSTRUMENTS = ["Voz", "Guitarra", "Violão", "Cavaco", "Baixo", "Bateria", "Teclado", "Saxofone", "Trompete", "Violino", "Percussão", "Pandeiro", "DJ"];

export default function MinhaEquipe() {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [instrument, setInstrument] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const loadTeam = () => { setLoadingTeam(true); api.get("/team/musicians").then((r) => setTeam(r.data)).catch(() => {}).finally(() => setLoadingTeam(false)); };
  useEffect(() => { loadTeam(); }, []);

  const favIds = new Set(team.map((t) => t.user_id));

  const search = async () => {
    if (!instrument) return toast.error("Escolha um instrumento para buscar.");
    setSearching(true);
    try {
      const { data } = await api.post("/match/musicians", { instrument, only_available: onlyAvailable, limit: 20 });
      setResults((data.results || []).map((m) => ({ ...m, is_favorite: favIds.has(m.user_id) })));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const toggleFav = async (m) => {
    try {
      if (favIds.has(m.user_id)) { await api.delete(`/team/musicians/${m.user_id}`); toast.success("Removido da equipe."); }
      else { await api.post(`/team/musicians/${m.user_id}`, {}); toast.success("Salvo na equipe!"); }
      loadTeam();
      setResults((prev) => prev ? prev.map((x) => x.user_id === m.user_id ? { ...x, is_favorite: !favIds.has(m.user_id) } : x) : prev);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeFromTeam = async (uid) => {
    try { await api.delete(`/team/musicians/${uid}`); toast.success("Removido da equipe."); loadTeam(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const togglePrimary = async (t) => {
    try { await api.put(`/team/musicians/${t.user_id}/primary`, { primary: !t.is_primary }); loadTeam(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-7 fade-up max-w-5xl" data-testid="minha-equipe-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Heart className="h-7 w-7 text-primary" />Minha Equipe</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Músicos favoritos para chamar rapidamente nos seus próximos eventos.</p>
        </div>
      </header>

      <section>
        <h2 className="font-head text-xl font-semibold tracking-tight mb-3">Equipe salva {team.length > 0 && <span className="text-muted-foreground font-normal">({team.length})</span>}</h2>
        {loadingTeam ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : team.length === 0 ? <EmptyState icon={Users} description="Você ainda não salvou músicos. Use a busca abaixo para montar sua equipe de confiança." />
          : (
            <div className="grid gap-2 sm:grid-cols-2">
              {team.map((t) => (
                <div key={t.user_id} data-testid={`team-member-${t.user_id}`} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center text-base font-bold shrink-0">
                    {t.photo ? <img src={t.photo} alt="" className="h-full w-full object-cover" /> : (t.stage_name?.[0] || "?")}
                  </div>
                  <button className="min-w-0 flex-1 text-left" onClick={() => navigate(`/musico/${t.user_id}`)}>
                    <div className="flex items-center gap-2"><span className="font-semibold truncate">{t.stage_name}</span>{t.is_primary && <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded">Principal</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{(t.instruments || []).join(", ") || "—"} · Score {t.score}</div>
                  </button>
                  <button data-testid={`primary-${t.user_id}`} onClick={() => togglePrimary(t)} title="Equipe principal" className={`h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${t.is_primary ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-primary"}`}><StarIcon className={`h-4 w-4 ${t.is_primary ? "fill-primary" : ""}`} /></button>
                  <button data-testid={`remove-${t.user_id}`} onClick={() => removeFromTeam(t.user_id)} title="Remover" className="h-9 w-9 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-red-400 hover:border-red-500/30 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="font-head text-xl font-semibold tracking-tight mb-1 flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />Buscar músicos (Match Inteligente)</h2>
        <p className="text-sm text-muted-foreground mb-4">Encontre profissionais por instrumento, ordenados por compatibilidade.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px] flex-1">
            <label className="text-xs text-muted-foreground">Instrumento</label>
            <Select value={instrument} onValueChange={setInstrument}>
              <SelectTrigger data-testid="match-instrument" className="mt-1 bg-[#09090B] border-border rounded-xl h-11"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-[#121217] border-border">{INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <button data-testid="match-only-available" onClick={() => setOnlyAvailable((v) => !v)} className={`h-11 px-4 rounded-xl border text-sm font-medium transition-colors ${onlyAvailable ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>Só disponíveis</button>
          <Button data-testid="match-search-btn" onClick={search} disabled={searching} className="h-11 gap-2">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Buscar</Button>
        </div>

        {results && (
          <div className="mt-5">
            {results.length === 0 ? <EmptyState icon={Search} description="Nenhum músico encontrado para este instrumento." />
              : <div className="grid gap-2 sm:grid-cols-2">{results.map((m) => <MusicianMatchCard key={m.user_id} m={m} onToggleFav={toggleFav} onView={() => navigate(`/musico/${m.user_id}`)} compact />)}</div>}
          </div>
        )}
      </section>
    </div>
  );
}
